import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type Header,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { type ReactNode } from 'react';

import { cx } from '@/lib/cx';

import { Paginacao, type PaginacaoProps } from './paginacao';
import { type ComportamentoLinha, Linha, type MetaColuna, Situacao } from './tabela-partes';

export interface Ordenacao {
  campo: string;
  ordem: 'asc' | 'desc';
}

export interface TabelaPaginadaProps<T>
  extends ComportamentoLinha<T>, Omit<PaginacaoProps, 'ocupado' | 'className'> {
  rotulo: string;
  /** `meta.ordenavel` = campo de ordenação no servidor; sem ele a coluna não ordena. */
  colunas: ColumnDef<T>[];
  /** Só a página atual — os dados já vêm paginados, filtrados e ordenados do servidor. */
  dados: readonly T[];
  idLinha: (linha: T) => string;
  ordenacao?: Ordenacao;
  aoOrdenar?: (campo: string) => void;
  /** Primeira carga (esqueleto). */
  carregando?: boolean;
  /** Trocando de página/ordem/filtro com a anterior na tela (esmaece, não pisca). */
  atualizando?: boolean;
  vazio: ReactNode;
  className?: string;
}

function IconeOrdem({ ordem }: { ordem: Ordenacao['ordem'] | null }) {
  if (ordem === 'asc') return <ArrowUp className="size-3.5 text-marca" aria-hidden />;
  if (ordem === 'desc') return <ArrowDown className="size-3.5 text-marca" aria-hidden />;
  return (
    <ChevronsUpDown
      className="size-3.5 opacity-0 transition-opacity duration-[var(--duracao-rapida)] group-hover/ordem:opacity-100 group-focus-visible/ordem:opacity-100"
      aria-hidden
    />
  );
}

const ARIA_SORT = { asc: 'ascending', desc: 'descending' } as const;

function BotaoOrdenar(p: {
  campo: string;
  ordem: Ordenacao['ordem'] | null;
  direita: boolean;
  aoOrdenar: (campo: string) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        p.aoOrdenar(p.campo);
      }}
      className={cx(
        'group/ordem -mx-1 inline-flex items-center gap-1 rounded-mini px-1 py-0.5 hover:text-texto focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-marca-suave',
        p.ordem && 'text-texto',
        p.direita && 'flex-row-reverse',
      )}
    >
      {p.children}
      <IconeOrdem ordem={p.ordem} />
    </button>
  );
}

function CabecalhoColuna<T>(p: {
  cabecalho: Header<T, unknown>;
  ordenacao: Ordenacao | undefined;
  aoOrdenar: ((campo: string) => void) | undefined;
}) {
  const meta = (p.cabecalho.column.columnDef.meta ?? {}) as MetaColuna;
  const ordem = meta.ordenavel && p.ordenacao?.campo === meta.ordenavel ? p.ordenacao.ordem : null;
  const titulo = flexRender(p.cabecalho.column.columnDef.header, p.cabecalho.getContext());
  const direita = meta.alinhar === 'direita';
  return (
    <th
      scope="col"
      style={meta.largura ? { width: meta.largura } : undefined}
      aria-sort={ordem ? ARIA_SORT[ordem] : undefined}
      className={cx(
        'border-b border-borda px-3 py-2.5 text-left text-xs font-medium text-texto-suave',
        direita && 'text-right',
      )}
    >
      {meta.ordenavel && p.aoOrdenar ? (
        <BotaoOrdenar
          campo={meta.ordenavel}
          ordem={ordem}
          direita={direita}
          aoOrdenar={p.aoOrdenar}
        >
          {titulo}
        </BotaoOrdenar>
      ) : (
        titulo
      )}
    </th>
  );
}

/**
 * Tabela padrão do sistema ("DataTable"): uma página por vez, ordenação por coluna e rodapé com
 * a faixa exibida, itens por página e navegação numerada. Busca e filtros ficam na barra da
 * página; tudo é resolvido no servidor (ADR 0024) — nunca carrega a lista inteira.
 */
export function TabelaPaginada<T>(props: TabelaPaginadaProps<T>) {
  const { colunas, dados, carregando = false, atualizando = false } = props;
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table devolve funções não memoizáveis; o componente não depende de memo.
  const tabela = useReactTable({
    data: dados as T[],
    columns: colunas,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (linha) => props.idLinha(linha),
    manualSorting: true,
    manualPagination: true,
  });
  const linhas = tabela.getRowModel().rows;
  const nColunas = colunas.length + (props.acoesLinha ? 1 : 0);
  return (
    <div
      className={cx(
        'flex min-h-0 flex-col overflow-hidden rounded-cartao border border-borda bg-superficie',
        props.className,
      )}
    >
      <div className="min-h-0 flex-1 overflow-auto">
        <table
          aria-label={props.rotulo}
          aria-busy={carregando || atualizando}
          className={cx(
            'w-full border-separate border-spacing-0 text-base transition-opacity duration-[var(--duracao-rapida)]',
            atualizando && 'opacity-60',
          )}
        >
          <thead className="sticky top-0 z-10 bg-superficie">
            {tabela.getHeaderGroups().map((grupo) => (
              <tr key={grupo.id}>
                {grupo.headers.map((cabecalho) => (
                  <CabecalhoColuna
                    key={cabecalho.id}
                    cabecalho={cabecalho}
                    ordenacao={props.ordenacao}
                    aoOrdenar={props.aoOrdenar}
                  />
                ))}
                {props.acoesLinha && (
                  <th scope="col" className="w-px border-b border-borda">
                    <span className="sr-only">Ações</span>
                  </th>
                )}
              </tr>
            ))}
          </thead>
          <tbody>
            <Situacao
              carregando={carregando}
              vazia={linhas.length === 0}
              colunas={nColunas}
              vazio={props.vazio}
            />
            {linhas.map((linha) => (
              <Linha key={linha.id} linha={linha} props={props} />
            ))}
          </tbody>
        </table>
      </div>
      {props.total > 0 && (
        <Paginacao
          className="border-t border-borda"
          pagina={props.pagina}
          porPagina={props.porPagina}
          total={props.total}
          aoMudarPagina={props.aoMudarPagina}
          aoMudarPorPagina={props.aoMudarPorPagina}
          ocupado={atualizando}
        />
      )}
    </div>
  );
}
